import React from 'react';
import './LowboyTimesheet.scss';
import { withRouter } from 'react-router-dom';
import { LinearProgress } from '@mui/material';
import { util } from '../Util';
import { ApiRoutes } from '../ApiRoutes';
import {
    Alert,
    Button,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
} from 'reactstrap';
import moment from 'moment';
import { MomentDateFormat } from '../MomentDateFormat';
import { LowboyAssignmentStatuses } from '../lowboyDispatch/LowboyAssignmentStatuses';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendar,
    faInfoCircle,
    faPencilRuler,
} from '@fortawesome/free-solid-svg-icons';
import { AppNavPaths } from '../AppNavPaths';
import LowboyTimesheetContracts from './LowboyTimesheetContracts';
import { toasty } from '../common/forms/FormElements';
import LowboyTimesheetActionBar from './LowboyTimesheetActionBar';
import { getTenantUserProfile } from '../common/TenantUserProfile';
import { hasAnyNotes } from './LowboyTimesheet';
import LowboyTimesheetNotesCard from './LowboyTimesheetNotesCard';
import LowboyTimesheetStatusRow from './LowboyTimesheetStatusRow';
import AdjustmentDetail from '../crewTimesheet/AdjustmentDetail';
import { TimesheetStatus } from '../timesheet/Timesheet';

class LowboyTimesheetForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            lowboyAssignments: [],
            equipments: [],
            equipmentTypes: [],
            equipmentLocations: [],
            forDate: new Date(),
            saving: false,
            lowboyTimesheet: null,
            anyChanges: false,
            adminEditMode: false,
        };
    }

    componentDidMount() {
        this.populateState();
    }

    async populateState() {
        const lowboyTimesheetId = this.props.match.params.lowboyTimesheetId;

        const [lowboyTimesheet, userProfile] = await Promise.all([
            util.fetch.js(ApiRoutes.lowboyTimesheet.get(lowboyTimesheetId)),
            getTenantUserProfile(),
        ]);

        const eventDate = moment(lowboyTimesheet.date).format(
            MomentDateFormat.ApiUrlRequest
        );

        const { userPermissions } = userProfile;

        const isAdmin = userPermissions.includes('lowboytimesheet.accept');

        const isTimesheetApprover = userPermissions.includes(
            'lowboytimesheet.approve'
        );

        const [
            lowboyAssignments,
            equipments,
            equipmentTypes,
            equipmentLocations,
        ] = await Promise.all([
            isAdmin
                ? util.fetch.get(
                      ApiRoutes.lowboyAssignment.associatedWith(
                          lowboyTimesheetId
                      )
                  )
                : util.fetch.get(
                      ApiRoutes.lowboyAssignment.dispatches(eventDate)
                  ),
            util.fetch.get(ApiRoutes.equipment.all()),
            util.fetch.get(ApiRoutes.equipmentType.all()),
            util.fetch.get(ApiRoutes.equipmentLocation.for(eventDate)),
        ]);

        this.setState({
            lowboyAssignments,
            loading: false,
            equipments,
            equipmentTypes,
            equipmentLocations,
            forDate: moment(lowboyTimesheet.date).toDate(),
            lowboyTimesheet,
            anyChanges: false,
            isAdmin,
            isTimesheetApprover,
            lowboyTimesheetId,
        });
    }

    areAnyAssignmentsNotConfirmed() {
        const { lowboyAssignments } = this.state;

        return lowboyAssignments.some(
            (la) =>
                la.lowboyAssignmentStatusId < LowboyAssignmentStatuses.Confirmed
        );
    }

    onTimesheetMutated = (lowboyTimesheet) => {
        this.setState({ lowboyTimesheet, anyChanges: true });
    };

    onSave = async () => {
        const { saving } = this.state;

        if (saving) {
            return;
        }

        this.setState({ saving: true });

        let { lowboyTimesheet } = this.state;

        try {
            lowboyTimesheet = await util.fetch.post(
                ApiRoutes.lowboyTimesheet.save(),
                lowboyTimesheet
            );

            toasty.success('Timesheet saved.');
        } catch (e) {
            console.error({ e });

            toasty.error('Error saving timesheet.');
        }

        this.setState({ saving: false, lowboyTimesheet, anyChanges: false });
    };

    onSubmit = async () => {
        const { saving } = this.state;

        if (saving) {
            return;
        }

        let { lowboyTimesheet } = this.state;

        try {
            lowboyTimesheet = await util.fetch.post(
                ApiRoutes.lowboyTimesheet.submit(),
                lowboyTimesheet
            );

            toasty.success('Timesheet submitted.');
        } catch (e) {
            console.error({ e });
            toasty.error('Failed to submit timesheet.');
        }

        this.setState({ saving: false, lowboyTimesheet, anyChanges: false });
    };

    onRecall = async () => {
        const { saving } = this.state;

        if (saving) {
            return;
        }

        let { lowboyTimesheet } = this.state;

        try {
            lowboyTimesheet = await util.fetch.post(
                ApiRoutes.lowboyTimesheet.recallForEdit(lowboyTimesheet.id)
            );

            toasty.success('Timesheet recalled.');
        } catch (e) {
            console.error({ e });
            toasty.error('Failed to recall timesheet.');
        }

        this.setState({ saving: false, lowboyTimesheet, anyChanges: false });
    };

    onApprove = async () => {
        const { saving } = this.state;

        if (saving) {
            return;
        }

        this.setState({ saving: true });

        const { lowboyTimesheetId } = this.state;

        try {
            const lowboyTimesheet = await util.fetch.post(
                ApiRoutes.lowboyTimesheetManagement.accept(lowboyTimesheetId)
            );

            toasty.success('Timesheet updated.');

            this.setState({ lowboyTimesheet });
        } catch (e) {
            console.error({ e });
            toasty.error('Error updating lowboy timesheet.');
        }

        this.setState({ saving: false });
    };

    onReject = async (notes) => {
        const { saving, lowboyTimesheetId } = this.state;

        if (saving || !notes) {
            return;
        }

        this.setState({ saving: true });

        try {
            const lowboyTimesheet = await util.fetch.post(
                ApiRoutes.lowboyTimesheetManagement.reject(),
                {
                    lowboyTimesheetId,
                    notes,
                }
            );

            toasty.success('Timesheet rejected.');

            this.setState({ lowboyTimesheet });
        } catch (e) {
            console.error({ e });
            toasty.error('Error rejecting lowboy timesheet.');
        }

        this.setState({ saving: false });
    };

    onToggleAdminEditMode = () => {
        this.setState((prev) => ({ adminEditMode: !prev.adminEditMode }));
    };

    renderBackButton = () => {
        const { isAdmin } = this.state;

        if (isAdmin) {
            return (
                <Button
                    className="w-100 mb-5"
                    color="info"
                    outline={true}
                    type="button"
                    onClick={() => {
                        const { history } = this.props;

                        history.push(AppNavPaths.TimesheetManagement);
                    }}
                >
                    <FontAwesomeIcon icon={faPencilRuler} className="mr-2" />
                    Back to Lowboy Timesheet Management
                </Button>
            );
        }

        return (
            <Button
                className="w-100 mb-2"
                color="info"
                outline={true}
                type="button"
                onClick={() => {
                    const { history } = this.props;

                    history.push(AppNavPaths.AssignmentCalendar);
                }}
            >
                <FontAwesomeIcon icon={faCalendar} className="mr-2" />
                Back to calendar
            </Button>
        );
    };

    getIsReadOnly = () => {
        const { lowboyTimesheet, isAdmin, isTimesheetApprover, adminEditMode } =
            this.state;

        if (!lowboyTimesheet) {
            return true;
        }

        if (isTimesheetApprover) {
            if (!adminEditMode) {
                return true;
            }

            return (
                lowboyTimesheet.timesheetStatusId >= TimesheetStatus.Approved
            );
        } else if (isAdmin) {
            if (!adminEditMode) {
                return true;
            }

            return (
                lowboyTimesheet.timesheetStatusId >= TimesheetStatus.Validated
            );
        }

        const isInPermittedStatus =
            lowboyTimesheet.timesheetStatusId == TimesheetStatus.Rejected ||
            lowboyTimesheet.timesheetStatusId == TimesheetStatus.New;

        return lowboyTimesheet.anyAdjustmentsMade || !isInPermittedStatus;
    };

    render() {
        const { loading, forDate, saving, lowboyTimesheet, isAdmin } =
            this.state;

        const isReadOnly = this.getIsReadOnly();

        if (loading || !lowboyTimesheet) {
            return <LinearProgress variant="indeterminate" color="secondary" />;
        }

        const anyAssignmentsNotConfirmed = this.areAnyAssignmentsNotConfirmed();
        const modifyingUsers = lowboyTimesheet?.modifyingUsers;

        return (
            <div className="assignments-container">
                {saving && (
                    <LinearProgress variant="indeterminate" color="secondary" />
                )}

                {this.renderBackButton()}

                {anyAssignmentsNotConfirmed && (
                    <Alert color="danger" fade={false}>
                        One or more unconfirmed assignments found.
                    </Alert>
                )}

                <AdjustmentDetail
                    entity={lowboyTimesheet}
                    modifyingUsers={modifyingUsers}
                    isAdmin={isAdmin}
                />

                <Card className="date-overview-card">
                    <CardHeader>
                        Lowboy timesheet: {lowboyTimesheet.employeeName}
                    </CardHeader>
                    <CardBody>
                        {moment(forDate).format(
                            MomentDateFormat.WeekDayNameAndDate
                        )}
                    </CardBody>
                    {hasAnyNotes(lowboyTimesheet) && (
                        <CardFooter>
                            <FontAwesomeIcon
                                icon={faInfoCircle}
                                className="mr-1"
                            />
                            This timesheet has notes.
                        </CardFooter>
                    )}
                </Card>

                <LowboyTimesheetStatusRow
                    {...this.state}
                    onRecall={this.onRecall}
                    isReadOnly={isReadOnly}
                    modifyingUsers={modifyingUsers}
                />

                <LowboyTimesheetContracts
                    {...this.state}
                    onTimesheetMutated={this.onTimesheetMutated}
                    isReadOnly={isReadOnly}
                    modifyingUsers={modifyingUsers}
                />

                <LowboyTimesheetNotesCard
                    onTimesheetMutated={this.onTimesheetMutated}
                    isReadOnly={isReadOnly}
                    {...this.state}
                />

                <LowboyTimesheetActionBar
                    {...this.state}
                    onSave={this.onSave}
                    onSubmit={this.onSubmit}
                    onApprove={this.onApprove}
                    onToggleAdminEditMode={this.onToggleAdminEditMode}
                    onReject={this.onReject}
                    isReadOnly={isReadOnly}
                    modifyingUsers={modifyingUsers}
                />

                {saving && (
                    <LinearProgress variant="indeterminate" color="secondary" />
                )}
            </div>
        );
    }
}

export default withRouter(LowboyTimesheetForm);
